import { MUICheckBox, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { MouseEvent, useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import { FieldRow, FormPage } from "../../../../components/FormPage"
import { ApplicationPermissionPost } from "../../../../models/ApplicationPermission"

const validationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  noDevice: z.boolean(),
})

export function ApplicationPermissionCreate() {
  const { applicationId } = useParams()
  const navigate = useNavigate()
  const returnURL = `/applications/${applicationId}#permissions`

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<ApplicationPermissionPost>({
    resolver: zodResolver(validationSchema),
  })

  useEffect(() => {
    reset({
      name: "",
      description: "",
      noDevice: false,
    })
  }, [reset])

  const onSubmit = (values: ApplicationPermissionPost) => digitalManagerApi
      .post(`/applications/${applicationId}/permissions`, {
        name: values.name,
        description: values.description,
        noDevice: values.noDevice,
      })
      .then((res) => {
        if (res.status === StatusCodes.CREATED) {
          toast.success("Permission created")
          navigate(returnURL)
        }
      })
      .catch(() => {
        toast.error("Cannot create permission")
      })

  const handleNameChange = useCallback(
    (field: ControllerRenderProps<ApplicationPermissionPost, "name">) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const handleDescriptionChange = useCallback(
    (field: ControllerRenderProps<ApplicationPermissionPost, "description">) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const handleToggle = useCallback(
    (field: ControllerRenderProps<ApplicationPermissionPost, "noDevice">) =>
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderCheckbox = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<ApplicationPermissionPost, "noDevice">
    }) => (
      <MUICheckBox
        id={field.name}
        checked={!!field.value}
        handleToggle={handleToggle(field)}
      />
    ),
    [handleToggle]
  )

  const renderNameField = useCallback(
    ({ field }: { field: ControllerRenderProps<ApplicationPermissionPost, "name"> }) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleNameChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name?.message, handleNameChange]
  )

  const renderDescriptionField = useCallback(
    (props: {
      field: ControllerRenderProps<ApplicationPermissionPost, "description">
    }) => {
      const { field } = props

      return (
        <MUITextField
          id="description"
          type="text"
          size={32}
          name={field.name}
          value={field.value}
          onChange={handleDescriptionChange(field)}
          onBlur={field.onBlur}
          isDisabled={field.disabled}
          errors={
            typeof errors.description?.message === "string"
              ? { description: { message: errors.description.message } }
              : undefined
          }
        />
      )
    },
    [errors.description?.message, handleDescriptionChange]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Name">
        <Controller name="name" control={control} render={renderNameField} />
      </FieldRow>
      <FieldRow label="Description">
        <Controller
          name="description"
          control={control}
          render={renderDescriptionField}
        />
      </FieldRow>
      <FieldRow label="No Device">
        <Controller name="noDevice" control={control} render={renderCheckbox} />
      </FieldRow>
    </FormPage>
  )
}
